// src/features/geo/geoSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { prettyName, canonicalName } from "../../utils/name";

const API_BASE = process.env.REACT_APP_API_URL || "";
const GEO_DEBUG = process.env.REACT_APP_GEO_DEBUG === "1";

/* ---------- helpers ---------- */

/**
 * Extract a property value from a GeoJSON feature using a list of possible keys.
 * Returns the raw (uncleaned) value of the first key that exists.
 */
const extractProp = (props, ...keys) => {
    for (const k of keys) {
        if (props[k] !== undefined && props[k] !== null) return props[k];
    }
    return null;
};

/**
 * Build a deduplicated, sorted list of prettyName display strings from features.
 * Uses canonicalName as the dedup key.
 */
const buildList = (features, ...keys) => {
    const seen = new Set();
    const result = [];
    for (const f of features) {
        const raw = extractProp(f.properties || {}, ...keys);
        if (raw === null) continue;
        const canon = canonicalName(raw);
        if (!canon || seen.has(canon)) continue;
        seen.add(canon);
        result.push(prettyName(raw));
    }
    result.sort((a, b) => a.localeCompare(b));
    if (GEO_DEBUG) {
        console.log("[geo] buildList keys:", keys, "→", result);
    }
    return result;
};

/* ---------- thunks ---------- */

// Per-thunk AbortControllers — cancel stale in-flight requests on rapid switching
let _districtAbort = null;
let _tehsilAbort = null;
let _villageAbort = null;

export const fetchDistricts = createAsyncThunk(
    "geo/fetchDistricts",
    async (stateName, { getState, rejectWithValue }) => {
        // Abort any previous in-flight districts fetch
        if (_districtAbort) _districtAbort.abort();
        _districtAbort = new AbortController();

        const cacheKey = stateName;
        const cached = getState().geo.cache.district[cacheKey];
        if (cached) {
            return { cacheKey, data: cached, fromCache: true };
        }
        try {
            const res = await fetch(
                `${API_BASE}/geodata/state?state=${encodeURIComponent(stateName)}`,
                { signal: _districtAbort.signal }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            return { cacheKey, data, fromCache: false };
        } catch (err) {
            if (err.name === 'AbortError') return rejectWithValue('aborted');
            return rejectWithValue(err.message);
        }
    }
);

export const fetchTehsils = createAsyncThunk(
    "geo/fetchTehsils",
    async ({ stateName, district }, { getState, rejectWithValue }) => {
        if (_tehsilAbort) _tehsilAbort.abort();
        _tehsilAbort = new AbortController();

        const cacheKey = `${stateName}__${district}`;
        const cached = getState().geo.cache.tehsil[cacheKey];
        if (cached) {
            return { cacheKey, data: cached, fromCache: true };
        }
        try {
            const res = await fetch(
                `${API_BASE}/geodata/tehsil?state=${encodeURIComponent(stateName)}&district=${encodeURIComponent(district)}`,
                { signal: _tehsilAbort.signal }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            return { cacheKey, data, fromCache: false };
        } catch (err) {
            if (err.name === 'AbortError') return rejectWithValue('aborted');
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVillages = createAsyncThunk(
    "geo/fetchVillages",
    async ({ stateName, district, tehsil }, { getState, rejectWithValue }) => {
        if (_villageAbort) _villageAbort.abort();
        _villageAbort = new AbortController();

        const cacheKey = `${stateName}__${district}__${tehsil}`;
        const cached = getState().geo.cache.village[cacheKey];
        if (cached) {
            return { cacheKey, data: cached, fromCache: true };
        }
        try {
            const res = await fetch(
                `${API_BASE}/geodata/village?state=${encodeURIComponent(stateName)}&district=${encodeURIComponent(district)}&tehsil=${encodeURIComponent(tehsil)}`,
                { signal: _villageAbort.signal }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            return { cacheKey, data, fromCache: false };
        } catch (err) {
            if (err.name === 'AbortError') return rejectWithValue('aborted');
            return rejectWithValue(err.message);
        }
    }
);

/* ---------- slice ---------- */

const initialState = {
    cache: { district: {}, tehsil: {}, village: {} },
    geojson: { district: null, tehsil: null, village: null },
    lists: { districts: [], tehsils: [], villages: [] },
    loading: { districts: false, tehsils: false, villages: false },
    error: { districts: null, tehsils: null, villages: null },
};

const geoSlice = createSlice({
    name: "geo",
    initialState,
    reducers: {
        clearGeo(state) {
            state.geojson = { district: null, tehsil: null, village: null };
            state.lists = { districts: [], tehsils: [], villages: [] };
            state.loading = { districts: false, tehsils: false, villages: false };
            state.error = { districts: null, tehsils: null, villages: null };
            // Note: cache is intentionally preserved
        },
    },
    extraReducers: (builder) => {
        /* ---- districts ---- */
        builder
            .addCase(fetchDistricts.pending, (state) => {
                state.loading.districts = true;
                state.error.districts = null;
                // Immediately clear downstream lists to prevent stale dropdown items
                state.lists.districts = [];
                state.lists.tehsils = [];
                state.lists.villages = [];
                state.geojson.tehsil = null;
                state.geojson.village = null;
            })
            .addCase(fetchDistricts.fulfilled, (state, action) => {
                const { cacheKey, data, fromCache } = action.payload;
                if (!fromCache) {
                    state.cache.district[cacheKey] = data;
                }
                state.geojson.district = data;
                state.lists.districts = buildList(
                    data.features || [],
                    "district",
                    "District",
                    "NAME_2"
                );
                state.loading.districts = false;
                // Clear child levels
                state.geojson.tehsil = null;
                state.geojson.village = null;
                state.lists.tehsils = [];
                state.lists.villages = [];
            })
            .addCase(fetchDistricts.rejected, (state, action) => {
                state.loading.districts = false;
                state.error.districts = action.payload || action.error.message;
            });

        /* ---- tehsils ---- */
        builder
            .addCase(fetchTehsils.pending, (state) => {
                state.loading.tehsils = true;
                state.error.tehsils = null;
                // Immediately clear downstream village list
                state.lists.tehsils = [];
                state.lists.villages = [];
                state.geojson.village = null;
            })
            .addCase(fetchTehsils.fulfilled, (state, action) => {
                const { cacheKey, data, fromCache } = action.payload;
                if (!fromCache) {
                    state.cache.tehsil[cacheKey] = data;
                }
                state.geojson.tehsil = data;
                state.lists.tehsils = buildList(
                    data.features || [],
                    "tehsil",
                    "subdist",
                    "NAME_3",
                    "NAME"
                );
                state.loading.tehsils = false;
                // Clear child levels
                state.geojson.village = null;
                state.lists.villages = [];
            })
            .addCase(fetchTehsils.rejected, (state, action) => {
                state.loading.tehsils = false;
                state.error.tehsils = action.payload || action.error.message;
            });

        /* ---- villages ---- */
        builder
            .addCase(fetchVillages.pending, (state) => {
                state.loading.villages = true;
                state.error.villages = null;
                // Immediately clear village list
                state.lists.villages = [];
            })
            .addCase(fetchVillages.fulfilled, (state, action) => {
                const { cacheKey, data, fromCache } = action.payload;
                if (!fromCache) {
                    state.cache.village[cacheKey] = data;
                }
                state.geojson.village = data;
                state.lists.villages = buildList(
                    data.features || [],
                    "village",
                    "NAME_4"
                );
                state.loading.villages = false;
            })
            .addCase(fetchVillages.rejected, (state, action) => {
                state.loading.villages = false;
                state.error.villages = action.payload || action.error.message;
            });
    },
});

export const { clearGeo } = geoSlice.actions;
export default geoSlice.reducer;
